import React, { useRef } from 'react';
import { Sky, Text } from '@react-three/drei';

import { Building } from './Building';
import { ClaudeLogo } from './ClaudeLogo';
import { Cloud } from './Cloud';
import { ContraMissionMarker } from './missions/contra';
import { CubeLogo } from './CubeLogo';
import { DirectionalLight } from 'three';
import { GrokLogo } from './GrokLogo';
import { Ground } from './Ground';
import { LibraryBuilding } from './LibraryBuilding';
import { LightDebugHelper } from '../utils/DebugControls';
import { MissionType } from '../App';
import { OrangeCloud } from './OrangeCloud';
import { Path } from './Path';
import { Rock } from './Rock';
import { Shrine } from './Shrine';
import { Tree } from './Tree';
import { Water } from './Water';
import { useDebugMode } from '../utils/DebugContext';

// Define prop types for Environment to receive player position
interface EnvironmentProps {
  playerPosition?: [number, number, number];
  onEnterMission?: (missionType: MissionType) => void;
}

export const Environment: React.FC<EnvironmentProps> = ({
  playerPosition = [0, 0, 0],
  onEnterMission,
}) => {
  const sunlightRef = useRef<DirectionalLight>(null!);
  const { isDebugMode } = useDebugMode();

  // Pass the mission entrance handler to the marker
  const handleEnterContraMission = () => {
    if (onEnterMission) {
      onEnterMission('contra');
    }
  };

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.4} color="#B3E5FC" />
      <directionalLight
        ref={sunlightRef}
        position={[50, 75, 50]}
        intensity={1.8}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={200}
        shadow-camera-left={-100}
        shadow-camera-right={100}
        shadow-camera-top={100}
        shadow-camera-bottom={-100}
      />
      {isDebugMode && <LightDebugHelper light={sunlightRef} />}
      {/* Sky & Low Poly Clouds - expanded with more coverage */}
      <Sky
        sunPosition={[50, 75, 50]}
        azimuth={0.25}
        turbidity={8}
        rayleigh={0.5}
      />
      {/* More clouds spread across the larger world */}
      <Cloud position={[-15, 18, -10]} scale={1.8} />
      <Cloud position={[10, 17, -15]} scale={2.2} />
      <Cloud position={[0, 19, -20]} scale={2.5} />
      <Cloud position={[-20, 16, -5]} scale={1.5} />
      <Cloud position={[18, 20, -8]} scale={2.0} />
      <Cloud position={[-30, 22, -25]} scale={2.3} />
      <Cloud position={[25, 21, -30]} scale={2.4} />
      <Cloud position={[40, 19, -15]} scale={2.1} />
      <Cloud position={[-35, 20, -40]} scale={2.2} />
      <Cloud position={[15, 23, -45]} scale={2.6} />
      {/* Additional clouds */}
      <Cloud position={[50, 18, -20]} scale={2.0} />
      <Cloud position={[-40, 19, -10]} scale={1.9} />
      <Cloud position={[35, 22, -50]} scale={2.4} />
      <Cloud position={[-45, 20, -30]} scale={2.2} />
      <Cloud position={[20, 17, -60]} scale={2.5} />
      {/* New clouds over distant areas */}
      <Cloud position={[70, 24, -70]} scale={2.7} />
      <Cloud position={[-60, 23, -60]} scale={2.6} />
      <Cloud position={[80, 25, -40]} scale={2.5} />
      <Cloud position={[-70, 22, -20]} scale={2.4} />
      <Cloud position={[60, 21, -80]} scale={2.8} />
      {/* Terrain - Ground component remains the same size but we'll populate more of it */}
      <Ground />
      {/* MAIN PATH NETWORK - Extended to connect all villages and landmarks */}
      {/* Original paths */}
      <Path from={[0, 0, 0]} to={[5, 0, -5]} />
      <Path from={[5, 0, -5]} to={[-5, 0, -10]} />
      <Path from={[-5, 0, -10]} to={[0, 0, -15]} />
      <Path from={[0, 0, -15]} to={[0, 0, -20]} />
      <Path from={[0, 0, -20]} to={[-10, 0, -30]} />
      <Path from={[-10, 0, -30]} to={[-20, 0, -25]} />
      <Path from={[0, 0, -20]} to={[15, 0, -25]} />
      <Path from={[15, 0, -25]} to={[20, 0, -35]} />
      <Path from={[5, 0, -5]} to={[15, 0, 0]} />
      <Path from={[15, 0, 0]} to={[25, 0, -10]} />
      <Path from={[-5, 0, -10]} to={[-15, 0, -5]} />
      {/* Existing extended paths */}
      <Path from={[15, 0, -25]} to={[25, 0, -40]} />
      <Path from={[25, 0, -40]} to={[40, 0, -45]} />
      <Path from={[-10, 0, -30]} to={[-30, 0, -40]} />
      <Path from={[25, 0, -10]} to={[35, 0, -5]} />
      <Path from={[35, 0, -5]} to={[45, 0, -15]} />
      <Path from={[-15, 0, -5]} to={[-25, 0, 5]} />
      <Path from={[-25, 0, 5]} to={[-35, 0, 10]} />
      {/* NEW PATHS TO CONNECT NEW VILLAGES AND LANDMARKS */}
      {/* Main Village to Hokage Mountain */}
      <Path from={[40, 0, -45]} to={[55, 0, -55]} />
      <Path from={[55, 0, -55]} to={[65, 0, -60]} />
      {/* Path to Training Grounds */}
      <Path from={[25, 0, -40]} to={[30, 0, -55]} />
      <Path from={[30, 0, -55]} to={[35, 0, -65]} />
      {/* Path to Waterfall Village */}
      <Path from={[-30, 0, -40]} to={[-45, 0, -55]} />
      <Path from={[-45, 0, -55]} to={[-60, 0, -65]} />
      {/* Path to Desert Outpost */}
      <Path from={[45, 0, -15]} to={[60, 0, -20]} />
      <Path from={[60, 0, -20]} to={[75, 0, -25]} />
      {/* Path to Forest Temple */}
      <Path from={[-35, 0, 10]} to={[-50, 0, 20]} />
      <Path from={[-50, 0, 20]} to={[-65, 0, 30]} />
      {/* Path to Hot Springs */}
      <Path from={[0, 0, -20]} to={[0, 0, -35]} />
      <Path from={[0, 0, -35]} to={[10, 0, -45]} />
      {/* Path to Fishing Village */}
      <Path from={[-20, 0, -25]} to={[-35, 0, -20]} />
      <Path from={[-35, 0, -20]} to={[-50, 0, -15]} />
      {/* Path to Bamboo Grove */}
      <Path from={[15, 0, 0]} to={[25, 0, 15]} />
      <Path from={[25, 0, 15]} to={[35, 0, 25]} />
      {/* ORIGINAL BUILDINGS */}
      <Building position={[0, 0, -20]} scale={1.5} />
      <Building position={[-10, 0, -15]} scale={1.2} />
      <Building position={[12, 0, -18]} scale={0.8} />
      <Building position={[-20, 0, -25]} scale={1.3} />
      <Building position={[20, 0, -35]} scale={1.7} />
      <Building position={[25, 0, -10]} scale={1.1} />
      <Building position={[-10, 0, -30]} scale={1.4} />
      {/* EXISTING VILLAGE AREAS */}
      {/* Major settlement */}
      <Building position={[40, 0, -45]} scale={1.6} />
      <Building position={[38, 0, -47]} scale={1.2} />
      <Building position={[42, 0, -43]} scale={1.0} />
      <Building position={[41, 0, -48]} scale={0.9} />
      <Building position={[37, 0, -42]} scale={1.3} />
      {/* Small outpost */}
      <Building position={[-30, 0, -40]} scale={1.4} />
      <Building position={[-28, 0, -42]} scale={1.0} />
      <Building position={[-32, 0, -38]} scale={0.8} />
      {/* Remote temple complex */}
      <Building position={[-35, 0, 10]} scale={1.8} />
      <Building position={[-38, 0, 12]} scale={1.2} />
      <Building position={[-32, 0, 8]} scale={1.0} />
      {/* Trading post */}
      <Building position={[45, 0, -15]} scale={1.5} />
      <Building position={[47, 0, -17]} scale={1.1} />
      <Building position={[43, 0, -13]} scale={0.9} />
      {/* NEW VILLAGES AND SETTLEMENTS */}
      {/* 1. HOKAGE MOUNTAIN VILLAGE - Prominent mountain with carved faces */}
      <Rock position={[65, 2, -60]} scale={3.0} type="boulder" />
      <Rock position={[67, 3, -62]} scale={3.2} type="boulder" />
      <Rock position={[63, 2.5, -58]} scale={3.1} type="boulder" />
      <Rock position={[66, 4, -61]} scale={3.5} type="boulder" />
      <Building position={[65, 0, -55]} scale={1.7} />
      <Building position={[63, 0, -57]} scale={1.5} />
      <Building position={[68, 0, -59]} scale={1.4} />
      <Building position={[67, 0, -54]} scale={1.3} />
      <Building position={[62, 0, -53]} scale={1.2} />
      {/* 2. TRAINING GROUNDS - Open area with practice dummies */}
      <Building position={[35, 0, -65]} scale={0.8} />
      <Building position={[33, 0, -67]} scale={0.7} />
      <Building position={[37, 0, -64]} scale={0.6} />
      <Tree position={[32, 0, -65]} type="pine" />
      <Tree position={[38, 0, -66]} type="pine" />
      <Rock position={[34, 0.5, -63]} scale={0.6} />
      <Rock position={[36, 0.5, -68]} scale={0.7} />
      {/* 3. WATERFALL VILLAGE - Hidden behind waterfalls */}
      <Water position={[-60, 0, -65]} scale={2.0} type="pond" />
      <Building position={[-58, 0, -63]} scale={1.3} />
      <Building position={[-62, 0, -66]} scale={1.2} />
      <Building position={[-57, 0, -68]} scale={1.1} />
      <Building position={[-63, 0, -64]} scale={1.0} />
      <Rock position={[-56, 2, -62]} scale={2.0} type="pointy" />
      <Rock position={[-64, 2.5, -67]} scale={2.2} type="boulder" />
      {/* 4. DESERT OUTPOST - Sandy area with cactus-like trees */}
      <Building position={[75, 0, -25]} scale={1.4} />
      <Building position={[77, 0, -27]} scale={1.2} />
      <Building position={[73, 0, -23]} scale={1.0} />
      <Building position={[76, 0, -22]} scale={0.9} />
      <Rock position={[74, 0.3, -28]} scale={0.8} type="flat" />
      <Rock position={[78, 0.4, -24]} scale={0.7} type="pointy" />
      <Tree position={[72, 0, -26]} type="dead" />
      <Tree position={[79, 0, -25]} type="dead" />
      {/* 5. FOREST TEMPLE - Ancient temple in deep forest */}
      <Building position={[-65, 0, 30]} scale={2.0} />
      <Building position={[-67, 0, 32]} scale={1.5} />
      <Building position={[-63, 0, 28]} scale={1.3} />
      <Tree position={[-66, 0, 27]} type="pine" />
      <Tree position={[-68, 0, 31]} type="pine" />
      <Tree position={[-62, 0, 33]} type="pine" />
      <Tree position={[-64, 0, 35]} type="pine" />
      <Tree position={[-69, 0, 29]} type="pine" />
      <Tree position={[-61, 0, 30]} type="pine" />
      {/* 6. HOT SPRINGS RESORT - Steamy ponds with relaxation areas */}
      <Water position={[10, 0, -45]} scale={1.5} type="pond" />
      <Water position={[12, 0, -48]} scale={1.2} type="pond" />
      <Water position={[8, 0, -42]} scale={1.3} type="pond" />
      <Building position={[9, 0, -47]} scale={1.1} />
      <Building position={[13, 0, -44]} scale={1.0} />
      <Building position={[7, 0, -46]} scale={0.9} />
      <Rock position={[11, 0.3, -43]} scale={0.6} type="flat" />
      <Rock position={[6, 0.4, -44]} scale={0.7} type="flat" />
      <OrangeCloud position={[9, 3, -46]} scale={0.6} /> {/* Steam effect */}
      <OrangeCloud position={[11, 3, -43]} scale={0.5} />
      {/* 7. FISHING VILLAGE - Near water with docks */}
      <Water position={[-50, 0, -15]} scale={1.8} type="lake" />
      <Building position={[-48, 0, -17]} scale={1.2} />
      <Building position={[-52, 0, -13]} scale={1.1} />
      <Building position={[-47, 0, -12]} scale={1.0} />
      <Building position={[-53, 0, -18]} scale={0.9} />
      {/* 8. BAMBOO GROVE - Serene meditation area */}
      <Tree position={[35, 0, 25]} type="pine" />
      <Tree position={[37, 0, 27]} type="pine" />
      <Tree position={[33, 0, 23]} type="pine" />
      <Tree position={[36, 0, 22]} type="pine" />
      <Tree position={[38, 0, 26]} type="pine" />
      <Tree position={[34, 0, 28]} type="pine" />
      <Tree position={[32, 0, 24]} type="pine" />
      <Rock position={[35, 0.2, 25]} scale={1.0} type="flat" />
      {/* NEW VILLAGES AND TOWNS SPREAD THROUGHOUT THE MAP */}
      {/* Northern Farm Village */}
      <Building position={[40, 0, 40]} scale={1.5} />
      <Building position={[42, 0, 42]} scale={1.3} />
      <Building position={[38, 0, 38]} scale={1.4} />
      <Building position={[43, 0, 39]} scale={1.2} />
      <Building position={[37, 0, 43]} scale={1.1} />
      <Tree position={[36, 0, 40]} type="pine" />
      <Tree position={[41, 0, 44]} type="pine" />
      <Tree position={[44, 0, 37]} />
      <Water position={[39, 0, 45]} scale={1.0} type="pond" />
      <Rock position={[45, 0.4, 41]} scale={0.8} />
      {/* Western Coastal Town */}
      <Building position={[-80, 0, 0]} scale={1.6} />
      <Building position={[-82, 0, 3]} scale={1.4} />
      <Building position={[-78, 0, -3]} scale={1.5} />
      <Building position={[-84, 0, -2]} scale={1.3} />
      <Building position={[-81, 0, -5]} scale={1.2} />
      <Building position={[-77, 0, 5]} scale={1.1} />
      <Water position={[-85, 0, 0]} scale={2.5} type="lake" />
      <Tree position={[-83, 0, 6]} type="autumn" />
      <Tree position={[-79, 0, 8]} type="autumn" />
      <Tree position={[-76, 0, -6]} />
      <Rock position={[-82, 0.6, -7]} scale={1.2} type="pointy" />
      {/* Eastern Mountain Village */}
      <Building position={[85, 0, 15]} scale={1.7} />
      <Building position={[87, 0, 18]} scale={1.5} />
      <Building position={[83, 0, 12]} scale={1.6} />
      <Building position={[89, 0, 14]} scale={1.4} />
      <Building position={[82, 0, 19]} scale={1.3} />
      <Rock position={[90, 2.0, 20]} scale={2.5} type="boulder" />
      <Rock position={[88, 1.8, 10]} scale={2.3} type="boulder" />
      <Rock position={[92, 2.2, 17]} scale={2.7} type="boulder" />
      <Rock position={[86, 1.9, 8]} scale={2.4} type="boulder" />
      <Tree position={[84, 0, 22]} type="pine" />
      <Tree position={[91, 0, 13]} type="pine" />
      {/* Southern Desert Settlement */}
      <Building position={[10, 0, -85]} scale={1.3} />
      <Building position={[13, 0, -88]} scale={1.2} />
      <Building position={[7, 0, -82]} scale={1.4} />
      <Building position={[15, 0, -84]} scale={1.1} />
      <Building position={[5, 0, -87]} scale={1.0} />
      <Tree position={[12, 0, -80]} type="dead" />
      <Tree position={[8, 0, -90]} type="dead" />
      <Tree position={[17, 0, -86]} type="dead" />
      <Rock position={[9, 0.5, -92]} scale={1.6} type="flat" />
      <Rock position={[14, 0.3, -83]} scale={1.2} type="flat" />
      {/* Northern Ice Village */}
      <Building position={[-20, 0, 70]} scale={1.5} />
      <Building position={[-22, 0, 73]} scale={1.3} />
      <Building position={[-18, 0, 67]} scale={1.4} />
      <Building position={[-23, 0, 69]} scale={1.2} />
      <Building position={[-17, 0, 74]} scale={1.1} />
      <Water position={[-25, 0, 72]} scale={1.8} type="pond" />
      <Rock position={[-19, 0.7, 76]} scale={1.5} type="pointy" />
      <Rock position={[-24, 0.6, 66]} scale={1.3} type="pointy" />
      <Tree position={[-21, 0, 65]} type="pine" />
      <Tree position={[-16, 0, 72]} type="pine" />
      {/* Southwestern Hidden Village */}
      <Building position={[-60, 0, -80]} scale={1.6} />
      <Building position={[-62, 0, -82]} scale={1.4} />
      <Building position={[-58, 0, -78]} scale={1.5} />
      <Building position={[-63, 0, -77]} scale={1.3} />
      <Building position={[-57, 0, -83]} scale={1.2} />
      <Water position={[-65, 0, -80]} scale={1.6} type="pond" />
      <Tree position={[-59, 0, -85]} type="dead" />
      <Tree position={[-64, 0, -75]} type="dead" />
      <Rock position={[-61, 0.8, -73]} scale={1.7} type="boulder" />
      <Rock position={[-56, 0.7, -86]} scale={1.5} type="boulder" />
      {/* DENSE FOREST AREAS THROUGHOUT THE MAP */}
      {/* Northern Forest */}
      <Tree position={[10, 0, 60]} />
      <Tree position={[15, 0, 62]} />
      <Tree position={[5, 0, 58]} />
      <Tree position={[8, 0, 65]} />
      <Tree position={[12, 0, 57]} />
      <Tree position={[18, 0, 59]} type="pine" />
      <Tree position={[3, 0, 63]} type="pine" />
      <Tree position={[7, 0, 61]} type="pine" />
      <Tree position={[14, 0, 67]} type="pine" />
      <Tree position={[9, 0, 56]} type="pine" />
      <Tree position={[16, 0, 64]} />
      <Tree position={[4, 0, 68]} />
      <Tree position={[11, 0, 69]} type="pine" />
      <Tree position={[19, 0, 66]} type="pine" />
      <Tree position={[2, 0, 55]} />
      {/* Eastern Forest */}
      <Tree position={[80, 0, -60]} type="pine" />
      <Tree position={[82, 0, -57]} type="pine" />
      <Tree position={[78, 0, -63]} type="pine" />
      <Tree position={[85, 0, -59]} type="pine" />
      <Tree position={[77, 0, -56]} />
      <Tree position={[83, 0, -64]} />
      <Tree position={[79, 0, -58]} />
      <Tree position={[86, 0, -62]} />
      <Tree position={[76, 0, -61]} />
      <Tree position={[84, 0, -55]} type="pine" />
      <Tree position={[81, 0, -65]} type="pine" />
      <Tree position={[87, 0, -60]} type="pine" />
      <Tree position={[75, 0, -58]} type="pine" />
      <Tree position={[88, 0, -63]} />
      <Tree position={[74, 0, -64]} />
      {/* Western Deep Forest */}
      <Tree position={[-70, 0, 50]} type="pine" />
      <Tree position={[-72, 0, 52]} type="pine" />
      <Tree position={[-68, 0, 48]} type="pine" />
      <Tree position={[-74, 0, 51]} type="pine" />
      <Tree position={[-67, 0, 53]} type="pine" />
      <Tree position={[-73, 0, 47]} type="pine" />
      <Tree position={[-69, 0, 54]} type="pine" />
      <Tree position={[-75, 0, 49]} type="pine" />
      <Tree position={[-66, 0, 46]} type="pine" />
      <Tree position={[-71, 0, 55]} type="pine" />
      <Tree position={[-76, 0, 53]} type="pine" />
      <Tree position={[-65, 0, 52]} type="pine" />
      <Tree position={[-77, 0, 48]} type="pine" />
      <Tree position={[-64, 0, 47]} type="pine" />
      <Tree position={[-78, 0, 51]} type="pine" />
      {/* Southern Mixed Forest */}
      <Tree position={[30, 0, -80]} type="autumn" />
      <Tree position={[33, 0, -82]} />
      <Tree position={[27, 0, -78]} type="autumn" />
      <Tree position={[35, 0, -81]} type="autumn" />
      <Tree position={[25, 0, -83]} />
      <Tree position={[38, 0, -79]} type="autumn" />
      <Tree position={[28, 0, -84]} />
      <Tree position={[36, 0, -77]} type="autumn" />
      <Tree position={[23, 0, -85]} type="autumn" />
      <Tree position={[31, 0, -75]} />
      <Tree position={[40, 0, -83]} type="autumn" />
      <Tree position={[29, 0, -87]} type="autumn" />
      <Tree position={[34, 0, -74]} />
      <Tree position={[24, 0, -76]} type="autumn" />
      <Tree position={[37, 0, -86]} />
      {/* Northwestern Edge Forest */}
      <Tree position={[-90, 0, 30]} type="autumn" />
      <Tree position={[-92, 0, 33]} type="autumn" />
      <Tree position={[-88, 0, 27]} type="autumn" />
      <Tree position={[-94, 0, 31]} type="autumn" />
      <Tree position={[-87, 0, 34]} type="autumn" />
      <Tree position={[-93, 0, 28]} type="autumn" />
      <Tree position={[-89, 0, 35]} type="autumn" />
      <Tree position={[-95, 0, 29]} type="autumn" />
      <Tree position={[-86, 0, 26]} type="autumn" />
      <Tree position={[-91, 0, 36]} type="autumn" />
      <Tree position={[-96, 0, 32]} type="autumn" />
      <Tree position={[-85, 0, 31]} type="autumn" />
      <Tree position={[-97, 0, 27]} type="autumn" />
      <Tree position={[-84, 0, 34]} type="autumn" />
      <Tree position={[-98, 0, 30]} type="autumn" />
      {/* Northeastern Pine Forest */}
      <Tree position={[60, 0, 80]} type="pine" />
      <Tree position={[63, 0, 83]} type="pine" />
      <Tree position={[57, 0, 77]} type="pine" />
      <Tree position={[65, 0, 81]} type="pine" />
      <Tree position={[55, 0, 84]} type="pine" />
      <Tree position={[68, 0, 78]} type="pine" />
      <Tree position={[58, 0, 85]} type="pine" />
      <Tree position={[66, 0, 76]} type="pine" />
      <Tree position={[53, 0, 82]} type="pine" />
      <Tree position={[61, 0, 79]} type="pine" />
      <Tree position={[69, 0, 86]} type="pine" />
      <Tree position={[56, 0, 75]} type="pine" />
      <Tree position={[64, 0, 87]} type="pine" />
      <Tree position={[52, 0, 81]} type="pine" />
      <Tree position={[67, 0, 74]} type="pine" />
      {/* Southeastern Dead Forest */}
      <Tree position={[40, 0, -90]} type="dead" />
      <Tree position={[43, 0, -92]} type="dead" />
      <Tree position={[37, 0, -88]} type="dead" />
      <Tree position={[45, 0, -91]} type="dead" />
      <Tree position={[35, 0, -93]} type="dead" />
      <Tree position={[48, 0, -89]} type="dead" />
      <Tree position={[38, 0, -94]} type="dead" />
      <Tree position={[46, 0, -87]} type="dead" />
      <Tree position={[33, 0, -95]} type="dead" />
      <Tree position={[41, 0, -86]} type="dead" />
      <Tree position={[49, 0, -93]} type="dead" />
      <Tree position={[36, 0, -91]} type="dead" />
      <Tree position={[44, 0, -96]} type="dead" />
      <Tree position={[32, 0, -88]} type="dead" />
      <Tree position={[47, 0, -85]} type="dead" />
      {/* ADDITIONAL SCATTERED TREES */}
      <Tree position={[-40, 0, 30]} />
      <Tree position={[45, 0, 20]} type="pine" />
      <Tree position={[70, 0, -40]} />
      <Tree position={[-50, 0, -70]} type="dead" />
      <Tree position={[55, 0, 35]} type="autumn" />
      <Tree position={[-75, 0, -15]} />
      <Tree position={[25, 0, 50]} type="pine" />
      <Tree position={[-30, 0, -60]} type="dead" />
      <Tree position={[90, 0, -10]} />
      <Tree position={[-60, 0, 60]} type="autumn" />
      <Tree position={[0, 0, 80]} type="pine" />
      <Tree position={[-85, 0, -40]} type="dead" />
      <Tree position={[75, 0, 60]} />
      <Tree position={[-10, 0, -75]} type="dead" />
      <Tree position={[95, 0, 25]} type="pine" />
      <Tree position={[-90, 0, 10]} type="autumn" />
      <Tree position={[30, 0, -95]} type="dead" />
      <Tree position={[-55, 0, 35]} />
      <Tree position={[85, 0, -75]} type="dead" />
      <Tree position={[-15, 0, 55]} type="pine" />
      {/* MORE ROCK FORMATIONS ACROSS THE MAP */}
      {/* Northern Rock Garden */}
      <Rock position={[0, 0.5, 70]} scale={1.2} type="flat" />
      <Rock position={[3, 0.6, 72]} scale={1.4} type="flat" />
      <Rock position={[-3, 0.4, 68]} scale={1.0} type="flat" />
      <Rock position={[5, 0.7, 73]} scale={1.6} type="flat" />
      <Rock position={[-5, 0.3, 75]} scale={0.8} type="flat" />
      <Rock position={[8, 0.5, 69]} scale={1.3} type="flat" />
      <Rock position={[-8, 0.8, 71]} scale={1.5} type="flat" />
      <Rock position={[10, 0.4, 74]} scale={1.1} type="flat" />
      <Rock position={[-10, 0.6, 67]} scale={1.7} type="flat" />
      {/* Eastern Canyon */}
      <Rock position={[90, 2.0, -30]} scale={3.0} type="boulder" />
      <Rock position={[93, 2.2, -33]} scale={3.2} type="boulder" />
      <Rock position={[87, 1.8, -27]} scale={2.8} type="boulder" />
      <Rock position={[95, 2.4, -35]} scale={3.4} type="boulder" />
      <Rock position={[85, 1.6, -25]} scale={2.6} type="boulder" />
      <Rock position={[97, 2.6, -28]} scale={3.6} type="boulder" />
      <Rock position={[89, 1.7, -38]} scale={2.7} type="boulder" />
      <Rock position={[94, 2.5, -23]} scale={3.5} type="boulder" />
      <Rock position={[83, 1.9, -31]} scale={2.9} type="boulder" />
      <Rock position={[92, 2.3, -36]} scale={3.3} type="boulder" />
      {/* Western Hills */}
      <Rock position={[-80, 1.5, -50]} scale={2.2} type="pointy" />
      <Rock position={[-83, 1.7, -53]} scale={2.4} type="pointy" />
      <Rock position={[-77, 1.3, -47]} scale={2.0} type="pointy" />
      <Rock position={[-85, 1.9, -55]} scale={2.6} type="pointy" />
      <Rock position={[-75, 1.1, -45]} scale={1.8} type="pointy" />
      <Rock position={[-87, 2.1, -48]} scale={2.8} type="pointy" />
      <Rock position={[-79, 1.4, -58]} scale={2.1} type="pointy" />
      <Rock position={[-84, 2.0, -43]} scale={2.7} type="pointy" />
      <Rock position={[-73, 1.2, -52]} scale={1.9} type="pointy" />
      <Rock position={[-82, 1.8, -56]} scale={2.5} type="pointy" />
      {/* Southern Scattered Boulders */}
      <Rock position={[20, 0.9, -70]} scale={1.8} type="boulder" />
      <Rock position={[25, 1.1, -75]} scale={2.0} type="boulder" />
      <Rock position={[15, 0.7, -65]} scale={1.6} type="boulder" />
      <Rock position={[28, 1.3, -72]} scale={2.2} type="boulder" />
      <Rock position={[12, 0.5, -78]} scale={1.4} type="boulder" />
      <Rock position={[23, 1.0, -63]} scale={1.9} type="boulder" />
      <Rock position={[18, 0.8, -80]} scale={1.7} type="boulder" />
      <Rock position={[30, 1.4, -68]} scale={2.3} type="boulder" />
      <Rock position={[10, 0.6, -73]} scale={1.5} type="boulder" />
      <Rock position={[26, 1.2, -77]} scale={2.1} type="boulder" />
      {/* MORE WATER FEATURES */}
      {/* Northern Lake System */}
      <Water position={[-10, 0, 90]} scale={2.0} type="lake" />
      <Water position={[-5, 0, 85]} scale={1.2} type="river" />
      <Water position={[-15, 0, 95]} scale={1.2} type="river" />
      <Water position={[0, 0, 88]} scale={1.8} type="pond" />
      <Water position={[-12, 0, 83]} scale={1.6} type="pond" />
      {/* Eastern River Network */}
      <Water position={[70, 0, 0]} scale={1.5} type="river" />
      <Water position={[75, 0, 5]} scale={1.5} type="river" />
      <Water position={[80, 0, 10]} scale={1.5} type="river" />
      <Water position={[85, 0, 5]} scale={1.5} type="river" />
      <Water position={[90, 0, 0]} scale={1.8} type="lake" />
      <Water position={[75, 0, -5]} scale={1.2} type="pond" />
      {/* Western Oasis */}
      <Water position={[-70, 0, -10]} scale={2.2} type="lake" />
      <Water position={[-75, 0, -5]} scale={1.4} type="pond" />
      <Water position={[-65, 0, -15]} scale={1.4} type="pond" />
      <Water position={[-72, 0, -20]} scale={1.6} type="pond" />
      <Water position={[-68, 0, 0]} scale={1.3} type="pond" />
      {/* Southern Wetlands */}
      <Water position={[-40, 0, -80]} scale={1.8} type="pond" />
      <Water position={[-35, 0, -85]} scale={1.7} type="pond" />
      <Water position={[-45, 0, -75]} scale={1.9} type="pond" />
      <Water position={[-30, 0, -83]} scale={1.6} type="pond" />
      <Water position={[-50, 0, -78]} scale={2.0} type="lake" />
      {/* Fix Shrine types to use only valid types */}
      <Shrine position={[-5, 0, -60]} scale={1.4} type="fire" />
      <Shrine position={[80, 0, -40]} scale={1.3} type="water" />
      <Shrine position={[20, 0, 20]} scale={1.2} type="earth" />
      <Shrine position={[-70, 0, -20]} scale={1.3} type="wind" />
      {/* Add more Shrines with valid types */}
      <Shrine position={[60, 0, 60]} scale={1.5} type="fire" />
      <Shrine position={[-90, 0, -60]} scale={1.4} type="water" />
      <Shrine position={[30, 0, -90]} scale={1.3} type="earth" />
      <Shrine position={[-50, 0, 80]} scale={1.5} type="wind" />
      {/* OpenAI Library Building - placed in a prominent location */}
      <Tree position={[100, 0, -20]} type="pine" />
      <Tree position={[100, 0, -18]} />
      <LibraryBuilding
        position={[90, 0, -25]}
        scale={1.2}
        rotation={[0, -Math.PI / 3, 0]}
      />
      {/* LOGOS PLACED RANDOMLY AROUND THE MAP */}
      <CubeLogo
        position={[-15, 2, 25]}
        scale={3}
        rotation={[0, Math.PI / 4, 0]}
      />
      <Text
        position={[-15, 4, 25]}
        rotation={[0, Math.PI / 4, 0]}
        fontSize={0.8}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
      >
        cursor.com
      </Text>
      <ClaudeLogo position={[65, 3, -35]} scale={3.5} rotation={[0, 0, 0]} />
      <Text
        position={[65, 5, -35]}
        rotation={[0, 0, 0]}
        fontSize={0.8}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
      >
        claude.ai
      </Text>
      <GrokLogo
        position={[-50, 2, -32]}
        scale={3}
        rotation={[Math.PI / 1, 0, 0]}
      />
      <Text
        position={[-50, 4, -32]}
        rotation={[0, Math.PI / 6, 0]}
        fontSize={0.8}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
      >
        grok.com
      </Text>
      <OrangeCloud position={[10, 6, -45]} scale={1.3} />
      <Text
        position={[10, 8, -45]}
        rotation={[0, 0, 0]}
        fontSize={0.8}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
      >
        cloudflare.com
      </Text>
      {/* Add a path to the library */}
      <Path from={[20, 0, -20]} to={[27, 0, -22]} />
      {/* Add some trees around the library for aesthetics */}
      <Tree position={[27, 0, -30]} type="pine" />
      <Tree position={[34, 0, -28]} />
      <Tree position={[32, 0, -20]} type="autumn" />
      {/* Add some decorative rocks near the entrance */}
      <Rock position={[25, 0.3, -21]} scale={0.5} />
      <Rock position={[32, 0.4, -24]} scale={0.6} />
      {/* NEW ADDITIONAL FORESTS AND SCATTERED TREES */}
      {/* Main central forest area */}
      <Tree position={[0, 0, -10]} type="pine" />
      <Tree position={[3, 0, -12]} />
      <Tree position={[-4, 0, -14]} type="pine" />
      <Tree position={[5, 0, -8]} type="pine" />
      <Tree position={[-3, 0, -6]} />
      <Tree position={[7, 0, -13]} type="pine" />
      <Tree position={[-8, 0, -9]} type="pine" />
      <Tree position={[2, 0, -5]} />
      <Tree position={[-5, 0, -11]} type="pine" />
      <Tree position={[9, 0, -7]} />
      <Tree position={[-7, 0, 4]} type="pine" />
      <Tree position={[6, 0, 3]} />
      <Tree position={[-2, 0, 2]} type="pine" />
      <Tree position={[8, 0, 123]} type="pine" />
      <Tree position={[-9, 0, 15]} />
      {/* Northeast dense mixed forest */}
      <Tree position={[50, 0, 50]} type="pine" />
      <Tree position={[51, 0, 47]} />
      <Tree position={[54, 0, 52]} type="pine" />
      <Tree position={[52, 0, 55]} />
      <Tree position={[48, 0, 48]} type="pine" />
      <Tree position={[53, 0, 45]} type="pine" />
      <Tree position={[47, 0, 53]} />
      <Tree position={[55, 0, 49]} type="pine" />
      <Tree position={[49, 0, 46]} />
      <Tree position={[56, 0, 54]} type="pine" />
      <Tree position={[46, 0, 51]} type="pine" />
      <Tree position={[57, 0, 47]} />
      <Tree position={[44, 0, 49]} type="pine" />
      <Tree position={[58, 0, 52]} />
      <Tree position={[45, 0, 55]} type="pine" />
      {/* Northwest tree cluster */}
      <Tree position={[-50, 0, 45]} />
      <Tree position={[-48, 0, 48]} type="pine" />
      <Tree position={[-52, 0, 50]} type="pine" />
      <Tree position={[-47, 0, 52]} />
      <Tree position={[-53, 0, 47]} type="pine" />
      <Tree position={[-51, 0, 43]} />
      <Tree position={[-49, 0, 51]} type="pine" />
      <Tree position={[-54, 0, 49]} />
      <Tree position={[-46, 0, 46]} type="pine" />
      <Tree position={[-55, 0, 44]} type="pine" />
      <Tree position={[-45, 0, 50]} />
      <Tree position={[-56, 0, 48]} type="pine" />
      <Tree position={[-44, 0, 43]} />
      <Tree position={[-57, 0, 46]} type="pine" />
      <Tree position={[-43, 0, 47]} />
      {/* Southeast dead tree area expansion */}
      <Tree position={[50, 0, -50]} type="dead" />
      <Tree position={[53, 0, -52]} type="dead" />
      <Tree position={[48, 0, -48]} type="dead" />
      <Tree position={[55, 0, -54]} type="dead" />
      <Tree position={[46, 0, -51]} type="dead" />
      <Tree position={[52, 0, -47]} type="dead" />
      <Tree position={[49, 0, -53]} type="dead" />
      <Tree position={[56, 0, -49]} type="dead" />
      <Tree position={[45, 0, -55]} type="dead" />
      <Tree position={[54, 0, -46]} type="dead" />
      <Tree position={[47, 0, -57]} type="dead" />
      <Tree position={[51, 0, -44]} type="dead" />
      <Tree position={[58, 0, -52]} type="dead" />
      <Tree position={[43, 0, -50]} type="dead" />
      <Tree position={[55, 0, -45]} type="dead" />
      {/* Southwest tree expansion */}
      <Tree position={[-45, 0, -45]} type="autumn" />
      <Tree position={[-42, 0, -48]} type="autumn" />
      <Tree position={[-47, 0, -43]} type="autumn" />
      <Tree position={[-44, 0, -50]} type="autumn" />
      <Tree position={[-49, 0, -46]} type="autumn" />
      <Tree position={[-41, 0, -44]} type="autumn" />
      <Tree position={[-48, 0, -51]} type="autumn" />
      <Tree position={[-43, 0, -47]} type="autumn" />
      <Tree position={[-50, 0, -42]} type="autumn" />
      <Tree position={[-45, 0, -52]} type="autumn" />
      <Tree position={[-40, 0, -49]} type="autumn" />
      <Tree position={[-46, 0, -41]} type="autumn" />
      <Tree position={[-51, 0, -48]} type="autumn" />
      <Tree position={[-39, 0, -45]} type="autumn" />
      <Tree position={[-52, 0, -44]} type="autumn" />
      {/* Central village more trees */}
      <Tree position={[2, 0, -18]} />
      <Tree position={[-33, 0, -22]} type="pine" />
      <Tree position={[35, 0, -25]} />
      <Tree position={[-37, 0, -18]} type="pine" />
      <Tree position={[34, 0, -12]} />
      <Tree position={[-35, 0, -15]} type="pine" />
      <Tree position={[47, 0, -21]} />
      <Tree position={[-42, 0, -28]} type="pine" />
      <Tree position={[49, 0, -15]} />
      <Tree position={[-48, 0, -25]} type="pine" />
      {/* Trees around eastern water bodies */}
      <Tree position={[72, 0, 3]} />
      <Tree position={[76, 0, 8]} type="pine" />
      <Tree position={[69, 0, -3]} type="pine" />
      <Tree position={[78, 0, 2]} />
      <Tree position={[71, 0, 7]} type="pine" />
      <Tree position={[77, 0, -5]} />
      <Tree position={[68, 0, 4]} type="pine" />
      <Tree position={[75, 0, 10]} />
      <Tree position={[73, 0, -7]} type="pine" />
      <Tree position={[79, 0, 6]} />
      {/* Trees around western water bodies */}
      <Tree position={[-72, 0, -8]} type="pine" />
      <Tree position={[-76, 0, -12]} />
      <Tree position={[-69, 0, -5]} type="pine" />
      <Tree position={[-78, 0, -7]} type="pine" />
      <Tree position={[-71, 0, -14]} />
      <Tree position={[-77, 0, -3]} type="pine" />
      <Tree position={[-68, 0, -9]} />
      <Tree position={[-74, 0, -6]} type="pine" />
      <Tree position={[-73, 0, -11]} />
      <Tree position={[-79, 0, -2]} type="pine" />
      {/* Northern edge forest fill */}
      <Tree position={[15, 0, 90]} type="pine" />
      <Tree position={[20, 0, 88]} />
      <Tree position={[12, 0, 85]} type="pine" />
      <Tree position={[25, 0, 92]} type="pine" />
      <Tree position={[8, 0, 87]} />
      <Tree position={[22, 0, 93]} type="pine" />
      <Tree position={[16, 0, 83]} />
      <Tree position={[28, 0, 89]} type="pine" />
      <Tree position={[5, 0, 91]} type="pine" />
      <Tree position={[23, 0, 84]} />
      <Tree position={[18, 0, 95]} type="pine" />
      <Tree position={[10, 0, 87]} />
      <Tree position={[27, 0, 82]} type="pine" />
      <Tree position={[3, 0, 94]} />
      <Tree position={[21, 0, 86]} type="pine" />
      {/* Southern edge forest fill */}
      <Tree position={[-15, 0, -90]} type="dead" />
      <Tree position={[-20, 0, -88]} type="dead" />
      <Tree position={[-12, 0, -85]} type="dead" />
      <Tree position={[-25, 0, -92]} type="dead" />
      <Tree position={[-8, 0, -87]} type="dead" />
      <Tree position={[-22, 0, -93]} type="dead" />
      <Tree position={[-16, 0, -83]} type="dead" />
      <Tree position={[-28, 0, -89]} type="dead" />
      <Tree position={[-5, 0, -91]} type="dead" />
      <Tree position={[-23, 0, -84]} type="dead" />
      <Tree position={[-18, 0, -95]} type="dead" />
      <Tree position={[-10, 0, -87]} type="dead" />
      <Tree position={[-27, 0, -82]} type="dead" />
      <Tree position={[-3, 0, -94]} type="dead" />
      <Tree position={[-21, 0, -86]} type="dead" />
      {/* Northeastern corner fill */}
      <Tree position={[85, 0, 85]} />
      <Tree position={[88, 0, 82]} type="pine" />
      <Tree position={[82, 0, 87]} type="pine" />
      <Tree position={[90, 0, 80]} />
      <Tree position={[80, 0, 90]} type="pine" />
      <Tree position={[87, 0, 88]} />
      <Tree position={[83, 0, 83]} type="pine" />
      <Tree position={[92, 0, 86]} type="pine" />
      <Tree position={[79, 0, 81]} />
      <Tree position={[89, 0, 93]} type="pine" />
      <Tree position={[81, 0, 78]} />
      <Tree position={[94, 0, 84]} type="pine" />
      <Tree position={[77, 0, 89]} type="pine" />
      <Tree position={[91, 0, 77]} />
      <Tree position={[84, 0, 92]} type="pine" />
      {/* Northwestern corner fill */}
      <Tree position={[-85, 0, 85]} type="autumn" />
      <Tree position={[-88, 0, 82]} type="autumn" />
      <Tree position={[-82, 0, 87]} type="autumn" />
      <Tree position={[-90, 0, 80]} type="autumn" />
      <Tree position={[-80, 0, 90]} type="autumn" />
      <Tree position={[-87, 0, 88]} type="autumn" />
      <Tree position={[-83, 0, 83]} type="autumn" />
      <Tree position={[-92, 0, 86]} type="autumn" />
      <Tree position={[-79, 0, 81]} type="autumn" />
      <Tree position={[-89, 0, 93]} type="autumn" />
      <Tree position={[-81, 0, 78]} type="autumn" />
      <Tree position={[-94, 0, 84]} type="autumn" />
      <Tree position={[-77, 0, 89]} type="autumn" />
      <Tree position={[-91, 0, 77]} type="autumn" />
      <Tree position={[-84, 0, 92]} type="autumn" />
      {/* Southeastern corner fill */}
      <Tree position={[85, 0, -85]} type="dead" />
      <Tree position={[88, 0, -82]} type="dead" />
      <Tree position={[82, 0, -87]} type="dead" />
      <Tree position={[90, 0, -80]} type="dead" />
      <Tree position={[80, 0, -90]} type="dead" />
      <Tree position={[87, 0, -88]} type="dead" />
      <Tree position={[83, 0, -83]} type="dead" />
      <Tree position={[92, 0, -86]} type="dead" />
      <Tree position={[79, 0, -81]} type="dead" />
      <Tree position={[89, 0, -93]} type="dead" />
      <Tree position={[81, 0, -78]} type="dead" />
      <Tree position={[94, 0, -84]} type="dead" />
      <Tree position={[77, 0, -89]} type="dead" />
      <Tree position={[91, 0, -77]} type="dead" />
      <Tree position={[84, 0, -92]} type="dead" />
      {/* Scattered small groups throughout */}
      <Tree position={[30, 0, 10]} type="pine" />
      <Tree position={[33, 0, 12]} />
      <Tree position={[28, 0, 14]} type="pine" />
      <Tree position={[-30, 0, 10]} />
      <Tree position={[-33, 0, 12]} type="pine" />
      <Tree position={[-28, 0, 14]} />
      <Tree position={[30, 0, -10]} />
      <Tree position={[33, 0, -12]} type="pine" />
      <Tree position={[28, 0, -14]} />
      <Tree position={[-30, 0, -10]} type="pine" />
      <Tree position={[-33, 0, -12]} />
      <Tree position={[-28, 0, -14]} type="pine" />
      <Tree position={[60, 0, 30]} type="autumn" />
      <Tree position={[62, 0, 33]} type="autumn" />
      <Tree position={[57, 0, 28]} type="autumn" />
      <Tree position={[-60, 0, 30]} type="autumn" />
      <Tree position={[-62, 0, 33]} type="autumn" />
      <Tree position={[-57, 0, 28]} type="autumn" />
      <Tree position={[60, 0, -30]} type="dead" />
      <Tree position={[62, 0, -33]} type="dead" />
      <Tree position={[57, 0, -28]} type="dead" />
      <Tree position={[-60, 0, -30]} type="dead" />
      <Tree position={[-62, 0, -33]} type="dead" />
      <Tree position={[-57, 0, -28]} type="dead" />
      {/* Mission Markers */}
      <group key={`mission-markers-${playerPosition.join(',')}`}>
        <ContraMissionMarker
          position={[-25, 0, 20]}
          playerPosition={playerPosition}
          onEnterMission={handleEnterContraMission}
        />
      </group>
    </>
  );
};
